import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';

const ACTIVE_CONTAINER_STATUSES = new Set<WorkflowContainerRuntimeNode['status']>([
  'running',
  'retrying',
  'pending',
  'blocked',
]);

function isDescendantContainer(
  containersById: Record<string, WorkflowContainerRuntimeNode>,
  ancestorId: string,
  nodeId: string,
): boolean {
  const seen = new Set<string>();
  let current = containersById[nodeId]?.parent_container_id ?? null;
  while (current) {
    if (current === ancestorId) return true;
    if (seen.has(current)) return false;
    seen.add(current);
    current = containersById[current]?.parent_container_id ?? null;
  }
  return false;
}

/** 子树内是否仍有未 settle 的容器（parallel mid-run 时 subworkflow 不得折叠）。 */
export function subtreeHasActiveWork(
  containersById: Record<string, WorkflowContainerRuntimeNode>,
  rootContainerId: string,
): boolean {
  for (const node of Object.values(containersById)) {
    if (node.container_id === rootContainerId) continue;
    if (!isDescendantContainer(containersById, rootContainerId, node.container_id)) continue;
    if (ACTIVE_CONTAINER_STATUSES.has(node.status)) return true;
  }
  return false;
}

export interface WorkflowStageListPartition {
  visible: WorkflowContainerRuntimeNode[];
  hiddenCount: number;
  total: number;
  /** 当前已展示的 done stage 数量 */
  visibleDoneCount: number;
  /** done stage 总数 */
  totalDoneCount: number;
}

function stageSortKey(node: WorkflowContainerRuntimeNode): number {
  return node.display?.progress_current ?? 0;
}

/**
 * P3：running / failed / pending / blocked 全显；
 * done 按 maxVisibleDoneCount 分页展示（默认最近 completedStagePageSize 条）。
 * maxVisibleDoneCount 由 WorkflowStageListPanel 的 "Show N more" 逐页递增。
 */
export function partitionWorkflowStages(
  stages: WorkflowContainerRuntimeNode[],
  maxVisibleDoneCount: number,
  containersById?: Record<string, WorkflowContainerRuntimeNode>,
): WorkflowStageListPartition {
  const sorted = [...stages].sort(
    (a, b) => stageSortKey(a) - stageSortKey(b) || a.scope_key.localeCompare(b.scope_key),
  );

  const always: WorkflowContainerRuntimeNode[] = [];
  const done: WorkflowContainerRuntimeNode[] = [];

  for (const stage of sorted) {
    if (
      containersById
      && subtreeHasActiveWork(containersById, stage.container_id)
    ) {
      always.push(stage);
      continue;
    }
    if (
      stage.status === 'running'
      || stage.status === 'retrying'
      || stage.status === 'failed'
      || stage.status === 'pending'
      || stage.status === 'blocked'
    ) {
      always.push(stage);
    } else if (stage.status === 'completed' || stage.status === 'skipped') {
      done.push(stage);
    } else {
      always.push(stage);
    }
  }

  const visibleDone = done.slice(Math.max(0, done.length - maxVisibleDoneCount));
  const visible = [...always, ...visibleDone].sort(
    (a, b) => stageSortKey(a) - stageSortKey(b) || a.scope_key.localeCompare(b.scope_key),
  );
  const hiddenCount = stages.length - visible.length;

  return {
    visible,
    hiddenCount,
    total: stages.length,
    visibleDoneCount: visibleDone.length,
    totalDoneCount: done.length,
  };
}

export function isActiveWorkflowContainer(
  node: WorkflowContainerRuntimeNode,
  activeLoopSessionId: string | null,
  activeContainerIds: string[],
): boolean {
  if (activeContainerIds.includes(node.container_id)) return true;
  if (
    node.status === 'completed'
    || node.status === 'failed'
    || node.status === 'skipped'
  ) {
    return false;
  }
  if (node.loopSessionId && node.loopSessionId === activeLoopSessionId) return true;
  return false;
}
